import { Box, styled } from '@mui/material'
import { useState } from 'react'
import SectionErrorBoundary from '@/components/SectionErrorBoundary'
import { useXmppUnreadCounts } from '@/features/dashboard/messaging/useXmppUnreadCounts'
import PartnerSuspendedBanner from '@/features/dashboard/partner/PartnerSuspendedBanner'
import EntityPeekProvider from '@/features/entity-peek/EntityPeekProvider'
import { AppSidebar } from './app-sidebar/AppSidebar'
import { AppTopBar } from './app-top-bar/AppTopBar'
import { BottomTabBar } from './bottom-tab-bar/BottomTabBar'
import { MobileMoreDrawer } from './mobile-more-drawer/MobileMoreDrawer'
import NavRoutes from './NavRoutes'

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 64

const LayoutRoot = styled(Box)(({ theme }) => ({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    width: '100%',
    // Account for sidebar on md+ — sidebar width is managed by Drawer permanently
    [theme.breakpoints.up('md')]: {
        // Dynamic offset handled inline via sx
    }
}))

const DashboardLayout = () => {
    useXmppUnreadCounts()
    const [moreOpen, setMoreOpen] = useState(false)

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Sidebar — hidden on mobile via its own display prop */}
            <AppSidebar />

            {/* Main content column */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0
                }}
            >
                <AppTopBar />

                <PartnerSuspendedBanner />

                {/* Page content — id is intentional for skip-nav link */}
                <Box
                    component="main"
                    id="main-content"
                    tabIndex={-1}
                    sx={{
                        flex: '1 1 auto',
                        // Bottom padding on mobile so content isn't hidden
                        // behind BottomTabBar (56px + its safe-area padding).
                        pb: {
                            xs: 'calc(56px + env(safe-area-inset-bottom, 0px))',
                            sm: 0
                        },
                        // viewport-fit=cover: keep page content out of the
                        // notch on a landscape iPhone. The sidebar pads the
                        // left edge itself when it is shown (sm and up).
                        pl: { xs: 'env(safe-area-inset-left, 0px)', sm: 0 },
                        pr: 'env(safe-area-inset-right, 0px)',
                        outline: 'none'
                    }}
                >
                    <SectionErrorBoundary label="dashboard-content">
                        {/* Any page can open a related record in a side
                            panel via <EntityLink>; see features/entity-peek. */}
                        <EntityPeekProvider>
                            <NavRoutes />
                        </EntityPeekProvider>
                    </SectionErrorBoundary>
                </Box>
            </Box>

            {/* Mobile navigation */}
            <BottomTabBar onMoreOpen={() => setMoreOpen(true)} />
            <MobileMoreDrawer
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
            />
        </Box>
    )
}

export default DashboardLayout
